import gsap from 'gsap';
import isTailwindBreakpoint from '../utils/isTailwindBreakpoint';

export default class FooterLinkList extends HTMLElement {
  private button: HTMLButtonElement | null = null;
  private container: HTMLDivElement | null = null;
  private content: HTMLUListElement | null = null;
  private icon: HTMLSpanElement | null = null;
  private items: HTMLLIElement[] | null = null;
  private isDesktop: boolean = isTailwindBreakpoint('lg') ?? false;

  private isOpen: boolean = false;
  private contentHeight: number = 0;

  private openTl: gsap.core.Timeline | null = null;
  private closeTl: gsap.core.Timeline | null = null;

  private onResizeBound = this.onResize.bind(this);
  private onButtonClickBound = this.onButtonClick.bind(this);

  static observedAttributes = ['data-is-open'];

  connectedCallback(): void {
    this.button = this.querySelector('[data-footer-link-list-ref="button"]');
    this.container = this.querySelector('[data-footer-link-list-ref="container"]');
    this.content = this.querySelector('[data-footer-link-list-ref="content"]');
    this.icon = this.querySelector('[data-footer-link-list-ref="icon"]');
    this.items = Array.from(this.querySelectorAll('[data-footer-link-list-ref="items"]')) as HTMLLIElement[];

    this.isOpen = this.dataset.isOpen === 'true';

    this.addEventListeners();
    this.onResize();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);

    if (this.button) {
      this.button.addEventListener('click', this.onButtonClickBound);
    }
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);

    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClickBound);
    }
  }

  onButtonClick(): void {
    if (this.isDesktop) {
      return;
    }

    this.dataset.isOpen = this.isOpen ? 'false' : 'true';
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name !== 'data-is-open' || oldValue === newValue) {
      return;
    }

    this.isOpen = newValue === 'true';

    if (this.isDesktop) {
      return;
    }

    if (this.isOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  onResize(): void {
    this.isDesktop = isTailwindBreakpoint('lg');

    if (this.content) {
      this.contentHeight = this.content.getBoundingClientRect().height;
    }

    if (this.isDesktop) {
      gsap.set(this.container, { height: 'auto' });
      gsap.set(this.items, { autoAlpha: 1 });
      return;
    } else {
      if (this.isOpen) {
        this.open(true);
      } else {
        this.close(true);
      }
    }

    if (this.isOpen) {
      gsap.set(this.container, { height: this.contentHeight });
    }
  }

  open(instant: boolean = false): void {
    this.isOpen = true;
    this.setExpanded(true);
    this.killTimelines();

    if (instant) {
      gsap.set(this.container, { height: this.contentHeight });
      gsap.set(this.icon, { scale: -1 });
      gsap.set(this.items, { autoAlpha: 1 });
      return;
    }

    this.openTl = gsap.timeline();
    this.openTl.to(this.container, { height: this.contentHeight, duration: 0.6, ease: 'power3.out' }, 0);
    this.openTl.set(this.icon, { scale: -1 }, 0);
    this.openTl.fromTo(
      this.items,
      {
        autoAlpha: 0,
      },
      {
        autoAlpha: 1,
        duration: 0.6,
        stagger: 0.075,
        ease: 'power2.out',
      },
      0,
    );
    this.openTl.fromTo(
      this.items,
      {
        y: 10,
      },
      {
        y: 0,
        duration: 0.6,
        stagger: 0.075,
        ease: 'power2.out',
      },
      0,
    );
  }

  close(instant: boolean = false): void {
    this.isOpen = false;
    this.setExpanded(false);
    this.killTimelines();

    if (instant) {
      gsap.set(this.container, { height: 0 });
      gsap.set(this.icon, { scale: 1 });
      gsap.set(this.items, { autoAlpha: 0 });
      return;
    }

    this.closeTl = gsap.timeline();
    this.closeTl.to(this.container, { height: 0, duration: 0.6, ease: 'expo.out' }, 0);
    this.closeTl.set(this.icon, { scale: 1 }, 0);
    this.closeTl.to(
      this.items,
      {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'power2.out',
      },
      0,
    );
  }

  setExpanded(expanded: boolean): void {
    if (this.button) {
      this.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  killTimelines(): void {
    if (this.openTl) {
      this.openTl.kill();
      this.openTl = null;
    }

    if (this.closeTl) {
      this.closeTl.kill();
      this.closeTl = null;
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.killTimelines();
  }
}

customElements.define('footer-link-list', FooterLinkList);
